/**
 * Chart of Accounts service.
 *
 * PPM owns a light catalog of "Charts of Accounts" (logical buckets of
 * G/L or cost-element accounts used in cost planning and forecasting).
 * Header rows live in Supabase (`chart_of_accounts`); member accounts
 * are cached in Supabase (`chart_of_accounts_accounts`) but the
 * source-of-truth for the account list is SAP S/4HANA:
 *
 *   - source='sap_chart_of_accounts'  → SKA1 filtered on KTOPL
 *   - source='sap_cost_element_group' → KSML / SETLEAF members for a
 *                                       controlling area + group name
 *   - source='manual'                 → hand-curated
 *
 * The two SAP fetchers hit standard A2X CDS services through the
 * existing /api/s4 proxy (browser → Vercel → ppm-s4-proxy → Cloud
 * Connector → S/4). If the sandbox doesn't have a given service
 * published, the call surfaces the HTTP error and the UI falls back
 * to manual entry — no silent failures.
 */

import { supabase } from '@/lib/supabase'

// Supabase PostgrestError is a plain object — String(err) on it returns
// "[object Object]". Use this to surface the real message in the UI.
export function describeCoaError(err: unknown): string {
  if (err instanceof Error) return err.message
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: unknown; hint?: unknown; code?: unknown }
    const msg = typeof e.message === 'string' ? e.message : ''
    const details = typeof e.details === 'string' ? e.details : ''
    const hint = typeof e.hint === 'string' ? e.hint : ''
    const code = typeof e.code === 'string' ? e.code : ''
    const combined = [msg, details, hint].filter(Boolean).join(' — ')
    if (combined) return code ? `${combined} [${code}]` : combined
    try { return JSON.stringify(err) } catch { /* fall through */ }
  }
  return String(err)
}

// ── Standard A2X service paths ──────────────────────────────────────────────
// Both are published by S/4HANA whitelisted scenarios; runtime URL on
// some kernels reports `srvd_a2x` but actually serves under `srvd` (see
// vibe-sap-recipes skill, gotchas section). We try the runtime-published
// path first.
const SAP_GL_IN_COA_PATH =
  '/api/s4/odata/v4/sap/api_glaccountinchartofaccounts/srvd_a2x/sap/api_glaccountinchartofaccounts/0001/GLAccountInChartOfAccounts'

// Cost-element groups are served by our standalone Z service
// ZUI_M12_COA_O4 → ZSD_M12_COA (package ZMACH12). We use the FULL SAP
// V4 runtime path (with the /sap/opu/odata4/ prefix baked in) so the
// request matches the broad Cloud Connector allow rule already in
// place for /sap/opu/odata4/sap/ — same pattern WbsUpdateService and
// SapHierarchyDeploymentService use.
//
// Published runtime URL reports srvd_a2x but this kernel serves under
// /srvd/ (see vibe-sap-recipes skill, V4 srvd_a2x → srvd quirk).
const SAP_CE_GROUP_BASE =
  '/api/s4/odata/v4/sap/opu/odata4/sap/zui_m12_coa_o4/srvd/sap/zsd_m12_coa/0001'

// ── Domain types ────────────────────────────────────────────────────────────
export type CoaSource = 'sap_chart_of_accounts' | 'sap_cost_element_group' | 'manual'

export type CoaAccountType =
  | 'PrimaryCost'
  | 'SecondaryCost'
  | 'Revenue'
  | 'BalanceSheet'
  | 'PL'
  | 'Statistical'

export interface ChartOfAccountsRow {
  id: string
  coaKey: string
  name: string
  description: string | null
  source: CoaSource
  sapControllingArea: string | null
  sapChartOfAccounts: string | null
  sapCostElementGroup: string | null
  currencyCode: string | null
  maintLanguage: string | null
  isDefault: boolean
  blocked: boolean
  sapLastSyncedAt: string | null
  sapLastSyncError: string | null
  createdAt: string
  updatedAt: string
  accountCount?: number
}

export interface CoaAccountRow {
  id: string
  chartOfAccountsId: string
  accountNo: string
  name: string | null
  shortName: string | null
  accountGroup: string | null      // immediate owning sub-group (e.g. "REV")
  groupPath: string | null         // full ancestor chain "ICS_ALL/ALLOW_ALL/REVCOGS/REV"
  groupLevel: number | null        // depth of the parent group (0 = root)
  accountType: CoaAccountType | null
  isBlocked: boolean
  isManualOverride: boolean
  sapLastSyncedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface ChartOfAccountsInput {
  coaKey: string
  name: string
  description?: string | null
  source: CoaSource
  sapControllingArea?: string | null
  sapChartOfAccounts?: string | null
  sapCostElementGroup?: string | null
  currencyCode?: string | null
  isDefault?: boolean
  blocked?: boolean
}

// ── Supabase row → camelCase ────────────────────────────────────────────────
interface CoaDbRow {
  id: string
  coa_key: string
  name: string
  description: string | null
  source: CoaSource
  sap_controlling_area: string | null
  sap_chart_of_accounts: string | null
  sap_cost_element_group: string | null
  currency_code: string | null
  maint_language: string | null
  is_default: boolean
  blocked: boolean
  sap_last_synced_at: string | null
  sap_last_sync_error: string | null
  created_at: string
  updated_at: string
}

function mapCoaRow(r: CoaDbRow): ChartOfAccountsRow {
  return {
    id: r.id,
    coaKey: r.coa_key,
    name: r.name,
    description: r.description,
    source: r.source,
    sapControllingArea: r.sap_controlling_area,
    sapChartOfAccounts: r.sap_chart_of_accounts,
    sapCostElementGroup: r.sap_cost_element_group,
    currencyCode: r.currency_code,
    maintLanguage: r.maint_language,
    isDefault: r.is_default,
    blocked: r.blocked,
    sapLastSyncedAt: r.sap_last_synced_at,
    sapLastSyncError: r.sap_last_sync_error,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  }
}

interface CoaAccountDbRow {
  id: string
  chart_of_accounts_id: string
  account_no: string
  name: string | null
  short_name: string | null
  account_group: string | null
  group_path: string | null
  group_level: number | null
  account_type: CoaAccountType | null
  is_blocked: boolean
  is_manual_override: boolean
  sap_last_synced_at: string | null
  created_at: string
  updated_at: string
}

function mapAccountRow(r: CoaAccountDbRow): CoaAccountRow {
  return {
    id: r.id,
    chartOfAccountsId: r.chart_of_accounts_id,
    accountNo: r.account_no,
    name: r.name,
    shortName: r.short_name,
    accountGroup: r.account_group,
    groupPath: r.group_path,
    groupLevel: r.group_level,
    accountType: r.account_type,
    isBlocked: r.is_blocked,
    isManualOverride: r.is_manual_override,
    sapLastSyncedAt: r.sap_last_synced_at,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  }
}

// ── Supabase CRUD: header ───────────────────────────────────────────────────

export async function getChartsOfAccounts(): Promise<ChartOfAccountsRow[]> {
  const { data, error } = await supabase
    .from('chart_of_accounts')
    .select('*')
    .order('coa_key', { ascending: true })
  if (error) throw error
  const rows = (data ?? []).map((r) => mapCoaRow(r as CoaDbRow))

  // Decorate with account counts (cheap second query — saves a per-row
  // round-trip in the UI). For tenants with many CoAs this could be
  // replaced with a view, but at config-manager scale (<100 rows)
  // a single group-by is fine.
  if (rows.length > 0) {
    const ids = rows.map((r) => r.id)
    const { data: counts } = await supabase
      .from('chart_of_accounts_accounts')
      .select('chart_of_accounts_id')
      .in('chart_of_accounts_id', ids)
    const tally = new Map<string, number>()
    for (const c of (counts ?? []) as Array<{ chart_of_accounts_id: string }>) {
      tally.set(c.chart_of_accounts_id, (tally.get(c.chart_of_accounts_id) ?? 0) + 1)
    }
    for (const r of rows) r.accountCount = tally.get(r.id) ?? 0
  }
  return rows
}

export async function getChartOfAccountsById(id: string): Promise<ChartOfAccountsRow | null> {
  const { data, error } = await supabase
    .from('chart_of_accounts')
    .select('*')
    .eq('id', id)
    .maybeSingle()
  if (error) throw error
  return data ? mapCoaRow(data as CoaDbRow) : null
}

export async function upsertChartOfAccounts(input: ChartOfAccountsInput & { id?: string }): Promise<ChartOfAccountsRow> {
  const payload = {
    id: input.id,
    coa_key: input.coaKey.trim(),
    name: input.name.trim(),
    description: input.description?.trim() || null,
    source: input.source,
    sap_controlling_area: input.sapControllingArea?.trim() || null,
    sap_chart_of_accounts: input.sapChartOfAccounts?.trim() || null,
    sap_cost_element_group: input.sapCostElementGroup?.trim() || null,
    currency_code: input.currencyCode?.trim() || null,
    is_default: !!input.isDefault,
    blocked: !!input.blocked,
  }

  // If marking this as default, clear any existing default first
  // (one-default partial unique index).
  if (payload.is_default) {
    const { error: clearErr } = await supabase
      .from('chart_of_accounts')
      .update({ is_default: false })
      .eq('is_default', true)
    if (clearErr) throw clearErr
  }

  const { data, error } = await supabase
    .from('chart_of_accounts')
    .upsert(payload, { onConflict: 'coa_key' })
    .select('*')
    .single()
  if (error) throw error
  return mapCoaRow(data as CoaDbRow)
}

export async function deleteChartOfAccounts(id: string): Promise<void> {
  const { error } = await supabase.from('chart_of_accounts').delete().eq('id', id)
  if (error) throw error
}

// ── Supabase CRUD: member accounts ─────────────────────────────────────────

export async function getCoaAccounts(coaId: string): Promise<CoaAccountRow[]> {
  const { data, error } = await supabase
    .from('chart_of_accounts_accounts')
    .select('*')
    .eq('chart_of_accounts_id', coaId)
    .order('account_no', { ascending: true })
  if (error) throw error
  return (data ?? []).map((r) => mapAccountRow(r as CoaAccountDbRow))
}

export async function upsertCoaAccount(input: {
  id?: string
  chartOfAccountsId: string
  accountNo: string
  name?: string | null
  accountGroup?: string | null
  accountType?: CoaAccountType | null
  isBlocked?: boolean
}): Promise<CoaAccountRow> {
  const payload = {
    id: input.id,
    chart_of_accounts_id: input.chartOfAccountsId,
    account_no: input.accountNo.trim(),
    name: input.name?.trim() ?? null,
    account_group: input.accountGroup?.trim() ?? null,
    group_path: input.accountGroup?.trim() ?? null,
    group_level: null,
    account_type: input.accountType ?? null,
    is_blocked: !!input.isBlocked,
    is_manual_override: true,
  }
  const { data, error } = await supabase
    .from('chart_of_accounts_accounts')
    .upsert(payload, { onConflict: 'chart_of_accounts_id,account_no' })
    .select('*')
    .single()
  if (error) throw error
  return mapAccountRow(data as CoaAccountDbRow)
}

export async function deleteCoaAccount(id: string): Promise<void> {
  const { error } = await supabase.from('chart_of_accounts_accounts').delete().eq('id', id)
  if (error) throw error
}

// ── Project association ────────────────────────────────────────────────────

export async function setProjectChartOfAccounts(projectId: string, coaId: string | null): Promise<void> {
  // Changing the CoA invalidates any prior scope (paths won't match a
  // different chart's tree), so clear it atomically.
  const { error } = await supabase
    .from('projects')
    .update({
      chart_of_accounts_id: coaId,
      chart_of_accounts_scope_path: null,
      chart_of_accounts_scope_group: null,
    })
    .eq('id', projectId)
  if (error) throw error
}

export async function setProjectCoaScope(
  projectId: string,
  scope: { groupName: string; groupPath: string } | null,
): Promise<void> {
  const { error } = await supabase
    .from('projects')
    .update({
      chart_of_accounts_scope_path: scope?.groupPath ?? null,
      chart_of_accounts_scope_group: scope?.groupName ?? null,
    })
    .eq('id', projectId)
  if (error) throw error
}

/**
 * The accounts a project may plan against, resolved from its assigned
 * Chart of Accounts and (optionally) narrowed to the project's scope
 * sub-group. This is what the forecasting tabs use to let planners book
 * Material and ODC lines to a real G/L / cost-element account.
 *
 * - `coa` is null when the project has no chart assigned (the tabs then
 *   fall back to free-text / category-only planning).
 * - `accounts` excludes blocked accounts and, when a scope path is set,
 *   keeps only accounts at-or-below that node in the group hierarchy.
 */
export interface ProjectCoaAccounts {
  coa: ChartOfAccountsRow | null
  scopePath: string | null
  scopeGroup: string | null
  accounts: CoaAccountRow[]
}

/** True when an account's group path sits at or below the scope node. */
export function isAccountInScope(account: CoaAccountRow, scopePath: string | null): boolean {
  if (!scopePath) return true
  const path = (account.groupPath ?? account.accountGroup ?? '').trim()
  if (!path) return false
  return path === scopePath || path.startsWith(`${scopePath}/`)
}

export async function getProjectCoaAccounts(projectId: string): Promise<ProjectCoaAccounts> {
  const { data, error } = await supabase
    .from('projects')
    .select('chart_of_accounts_id, chart_of_accounts_scope_path, chart_of_accounts_scope_group')
    .eq('id', projectId)
    .maybeSingle()
  if (error) throw error

  const row = (data as ProjectCoaRow | null) ?? null
  const coaId = row?.chart_of_accounts_id ?? null
  const scopePath = row?.chart_of_accounts_scope_path ?? null
  const scopeGroup = row?.chart_of_accounts_scope_group ?? null

  if (!coaId) {
    return { coa: null, scopePath: null, scopeGroup: null, accounts: [] }
  }

  const [coa, allAccounts] = await Promise.all([
    getChartOfAccountsById(coaId),
    getCoaAccounts(coaId),
  ])

  const accounts = allAccounts
    .filter((a) => !a.isBlocked && isAccountInScope(a, scopePath))
    .sort((a, b) => a.accountNo.localeCompare(b.accountNo))

  return { coa, scopePath, scopeGroup, accounts }
}

interface ProjectCoaRow {
  chart_of_accounts_id: string | null
  chart_of_accounts_scope_path: string | null
  chart_of_accounts_scope_group: string | null
}

/**
 * Group node derived from the cached accounts of a CoA. Each row's
 * `group_path` indexes it into the tree; we also return the count of
 * accounts whose path starts with this node so the UI can show
 * "N accounts" badges next to each branch.
 */
export interface CoaGroupTreeNode {
  name: string
  fullPath: string
  level: number
  ownAccountCount: number       // accounts directly under this group
  descendantAccountCount: number // accounts under this group or any descendant
  children: CoaGroupTreeNode[]
}

export async function getCoaGroupTree(coaId: string): Promise<CoaGroupTreeNode[]> {
  const accounts = await getCoaAccounts(coaId)
  return buildGroupTreeFromAccounts(accounts)
}

export function buildGroupTreeFromAccounts(accounts: CoaAccountRow[]): CoaGroupTreeNode[] {
  type Node = {
    name: string
    fullPath: string
    level: number
    ownAccountCount: number
    children: Map<string, Node>
  }
  const roots = new Map<string, Node>()

  function ensure(name: string, fullPath: string, level: number, parent: Map<string, Node>): Node {
    let n = parent.get(name)
    if (!n) {
      n = { name, fullPath, level, ownAccountCount: 0, children: new Map() }
      parent.set(name, n)
    }
    return n
  }

  for (const a of accounts) {
    const path = (a.groupPath ?? a.accountGroup ?? '').trim()
    if (!path) continue
    const segments = path.split('/').filter(Boolean)
    let parent: Map<string, Node> = roots
    let acc = ''
    let depth = 0
    let leaf: Node | null = null
    for (const seg of segments) {
      acc = depth === 0 ? seg : `${acc}/${seg}`
      const node = ensure(seg, acc, depth, parent)
      parent = node.children
      leaf = node
      depth++
    }
    if (leaf) leaf.ownAccountCount += 1
  }

  function finalize(n: Node): CoaGroupTreeNode {
    const children = Array.from(n.children.values())
      .map(finalize)
      .sort((a, b) => a.name.localeCompare(b.name))
    const descendantCount = n.ownAccountCount + children.reduce((s, c) => s + c.descendantAccountCount, 0)
    return {
      name: n.name,
      fullPath: n.fullPath,
      level: n.level,
      ownAccountCount: n.ownAccountCount,
      descendantAccountCount: descendantCount,
      children,
    }
  }

  return Array.from(roots.values())
    .map(finalize)
    .sort((a, b) => a.name.localeCompare(b.name))
}

// ── SAP fetchers (read-only, via /api/s4 proxy) ────────────────────────────

interface ODataCollection<T> { value: T[] }
interface ODataError { error?: { code?: string; message?: string | { value?: string } } }

async function sapGet<T>(url: string): Promise<T[]> {
  const res = await fetch(url, { headers: { Accept: 'application/json' } })
  if (!res.ok) {
    let msg = `SAP HTTP ${res.status}`
    try {
      const body = (await res.json()) as ODataError
      const m = body?.error?.message
      msg = (typeof m === 'string' ? m : m?.value) ?? msg
    } catch {
      try { msg = (await res.text()) || msg } catch { /* ignore */ }
    }
    throw new Error(`${msg} (${url})`)
  }
  const body = (await res.json()) as ODataCollection<T>
  return body.value ?? []
}

// Standard SAP GL account row shape (truncated to the fields we use).
interface SapGlAccount {
  ChartOfAccounts?: string
  GLAccount: string
  GLAccountName?: string
  GLAccountShortName?: string
  GLAccountGroup?: string
  GLAccountType?: string                 // 'N' nonopex, 'P' primary cost, 'S' secondary, 'X' B/S, 'R' revenue, 'C' cash
  IsBlockedForPosting?: boolean
}

function mapSapGlType(t: string | undefined): CoaAccountType | null {
  switch ((t ?? '').toUpperCase()) {
    case 'P': return 'PrimaryCost'
    case 'S': return 'SecondaryCost'
    case 'R': return 'Revenue'
    case 'X': return 'BalanceSheet'
    case 'N': return 'PL'
    default:  return t ? null : null
  }
}

/** Fetch every G/L account assigned to a SAP Chart of Accounts (KTOPL). */
export async function listGlAccountsFromCoa(coaKey: string): Promise<CoaAccountRow[]> {
  const filter = `ChartOfAccounts eq '${coaKey.replace(/'/g, "''")}'`
  const url = `${SAP_GL_IN_COA_PATH}?$filter=${encodeURIComponent(filter)}&$top=2000`
  const rows = await sapGet<SapGlAccount>(url)
  return rows.map((r) => ({
    id: '',
    chartOfAccountsId: '',
    accountNo: r.GLAccount,
    name: r.GLAccountName ?? null,
    shortName: r.GLAccountShortName ?? null,
    accountGroup: r.GLAccountGroup ?? null,
    groupPath: r.GLAccountGroup ?? null,
    groupLevel: r.GLAccountGroup ? 0 : null,
    accountType: mapSapGlType(r.GLAccountType),
    isBlocked: !!r.IsBlockedForPosting,
    isManualOverride: false,
    sapLastSyncedAt: new Date().toISOString(),
    createdAt: '',
    updatedAt: '',
  }))
}

// Row shape exposed by ZUI_M12_COA_O4 / CoaGroupMembers. The service
// expands SETLEAF intervals against CSKB so every returned row is an
// individual cost element; ParentGroup is the immediate sub-group of
// the requested root that owns the leaf interval.
interface SapCeGroupMember {
  ControllingArea: string
  CostElementGroup: string                // the root group the user requested
  ParentGroup?: string                    // immediate owning sub-group (e.g. UA_EXP under ICS_ALL)
  GroupPath?: string                      // full ancestor chain "ICS_ALL/ALLOW_ALL/.../REV"
  GroupLevel?: number                     // depth of the parent group (0 = root)
  CostElement: string
  CostElementName?: string
  CostElementType?: string                // '01' primary, '11' revenue, '43' secondary
  IsBlocked?: string                      // '' or 'X'
}

function mapSapCostElementType(t: string | undefined): CoaAccountType | null {
  switch ((t ?? '').trim()) {
    case '01': return 'PrimaryCost'
    case '11': return 'Revenue'
    case '12': return 'PL'
    case '22': return 'PL'
    case '43': return 'SecondaryCost'
    case '90': return 'Statistical'
    default:   return null
  }
}

/**
 * Fetch the leaf members of a SAP Cost Element Group. Hits the
 * parameterised entity-set ZUI_M12_COA_O4/CoaGroupMembers, which
 * already expands SETLEAF intervals against CSKB on the SAP side.
 */
export async function listMembersFromCostElementGroup(
  controllingArea: string,
  groupName: string,
): Promise<CoaAccountRow[]> {
  const esc = (v: string) => v.replace(/'/g, "''")
  const url =
    `${SAP_CE_GROUP_BASE}/CoaGroupMembers(P_ControllingArea='${esc(controllingArea)}',P_CostElementGroup='${esc(groupName)}')/Set?$top=5000`
  const rows = await sapGet<SapCeGroupMember>(url)
  return rows.map((r) => {
    const trimmedName = r.CostElementName?.trim()
    const parent = r.ParentGroup?.trim()
    const path = r.GroupPath?.trim()
    return {
      id: '',
      chartOfAccountsId: '',
      accountNo: r.CostElement,
      name: trimmedName ? trimmedName : null,
      shortName: null,
      accountGroup: parent || groupName,
      groupPath: path || groupName,
      groupLevel: typeof r.GroupLevel === 'number' ? r.GroupLevel : null,
      accountType: mapSapCostElementType(r.CostElementType),
      isBlocked: (r.IsBlocked ?? '').trim() === 'X',
      isManualOverride: false,
      sapLastSyncedAt: new Date().toISOString(),
      createdAt: '',
      updatedAt: '',
    }
  })
}

// ── Composite "sync from SAP" — refreshes the cached member list ───────────

export interface CoaSyncResult {
  fetched: number
  inserted: number
  updated: number
  removed: number
}

/**
 * Refresh the cached member list for a CoA from SAP. Existing manually-
 * added rows (is_manual_override=true) are preserved. Rows previously
 * sourced from SAP that no longer appear in the latest fetch are removed.
 */
export async function syncCoaFromSap(coaId: string): Promise<CoaSyncResult> {
  const coa = await getChartOfAccountsById(coaId)
  if (!coa) throw new Error(`Chart of Accounts ${coaId} not found.`)
  if (coa.source === 'manual') {
    throw new Error('Manual charts of accounts cannot be synced from SAP.')
  }

  let fetched: CoaAccountRow[] = []
  try {
    if (coa.source === 'sap_chart_of_accounts') {
      if (!coa.sapChartOfAccounts) throw new Error('SAP Chart of Accounts key (KTOPL) is required.')
      fetched = await listGlAccountsFromCoa(coa.sapChartOfAccounts)
    } else {
      if (!coa.sapControllingArea || !coa.sapCostElementGroup) {
        throw new Error('Controlling Area and Cost Element Group are required.')
      }
      fetched = await listMembersFromCostElementGroup(coa.sapControllingArea, coa.sapCostElementGroup)
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    await supabase
      .from('chart_of_accounts')
      .update({ sap_last_sync_error: msg, sap_last_synced_at: new Date().toISOString() })
      .eq('id', coaId)
    throw err
  }

  // Diff against existing cache: keep manual overrides untouched, upsert
  // everything else, remove SAP rows that vanished.
  const existing = await getCoaAccounts(coaId)
  const existingByNo = new Map(existing.map((r) => [r.accountNo, r]))
  const fetchedNos = new Set(fetched.map((r) => r.accountNo))

  const now = new Date().toISOString()
  const toUpsert = fetched.map((r) => ({
    chart_of_accounts_id: coaId,
    account_no: r.accountNo,
    name: r.name,
    short_name: r.shortName,
    account_group: r.accountGroup,
    group_path: r.groupPath,
    group_level: r.groupLevel,
    account_type: r.accountType,
    is_blocked: r.isBlocked,
    is_manual_override: existingByNo.get(r.accountNo)?.isManualOverride ?? false,
    sap_last_synced_at: now,
  }))

  let inserted = 0
  let updated = 0
  if (toUpsert.length > 0) {
    const { error } = await supabase
      .from('chart_of_accounts_accounts')
      .upsert(toUpsert, { onConflict: 'chart_of_accounts_id,account_no' })
    if (error) throw error
    for (const r of toUpsert) {
      if (existingByNo.has(r.account_no)) updated++
      else inserted++
    }
  }

  const orphans = existing.filter((r) => !r.isManualOverride && !fetchedNos.has(r.accountNo))
  if (orphans.length > 0) {
    const { error } = await supabase
      .from('chart_of_accounts_accounts')
      .delete()
      .in('id', orphans.map((o) => o.id))
    if (error) throw error
  }

  await supabase
    .from('chart_of_accounts')
    .update({
      sap_last_synced_at: now,
      sap_last_sync_error: null,
    })
    .eq('id', coaId)

  return { fetched: fetched.length, inserted, updated, removed: orphans.length }
}
